import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
    disablePushNotifications,
    enablePushNotifications,
    getPermission,
    isPushSupported,
    listenForPushNavigation,
    syncPushSubscription
} from './pushNotifications'

/**
 * Push notifications for the logged-in user.
 *
 *   const push = usePushNotifications( { apiClient, audience: 'user' } )        // students
 *   const push = usePushNotifications( { apiClient, audience: 'coordinator' } ) // coordinators / admins
 *
 *   push.supported   false in browsers without Web Push (e.g. iOS Safari unless the site is installed)
 *   push.permission  'default' | 'granted' | 'denied' | 'unsupported'
 *   push.subscribed  this browser is receiving notifications
 *   push.enable()    call from a click (shows the browser's permission prompt)
 *   push.disable()   turn off for this browser
 *   push.busy, push.error
 *
 * Mount it once, inside the router, for logged-in users only (e.g. in the authenticated layout). It re-saves the
 * subscription on load and turns notification clicks into in-app navigation.
 */
const usePushNotifications = ( { apiClient, audience = 'user', enabled = true } = {} ) => {
    const navigate = useNavigate()
    const supported = isPushSupported()

    const [permission, setPermission] = useState( getPermission )
    const [subscribed, setSubscribed] = useState( true )
    const [busy, setBusy] = useState( false )
    const [error, setError] = useState( null )

    // Re-save the subscription for the current user on every load (no prompt)
    useEffect( () => {
        if ( !enabled || !supported || !apiClient ) return

        let cancelled = false

        syncPushSubscription( apiClient, audience )
            .then( isSubscribed => { if ( !cancelled ) setSubscribed( isSubscribed ) } )
            .catch( err => { if ( !cancelled ) setError( err ) } )

        return () => { cancelled = true }
    }, [enabled, supported, apiClient, audience] )

    // Notification clicks open the page inside this tab
    useEffect( () => {
        if ( !enabled ) return
        return listenForPushNavigation( navigate )
    }, [enabled, navigate] )

    const enable = useCallback( async () => {
        setBusy( true )
        setError( null )

        try {
            const result = await enablePushNotifications( apiClient, audience )
            setPermission( getPermission() )
            setSubscribed( result === 'granted' )
            console.log( result )

            return result
        } catch ( err ) {
            setError( err )
            throw err
        } finally {
            setBusy( false )
            console.log( "Ran enable!" )

        }
    }, [apiClient, audience] )

    const disable = useCallback( async () => {
        setBusy( true )
        setError( null )

        try {
            await disablePushNotifications( apiClient, audience )
            setSubscribed( false )
        } catch ( err ) {
            setError( err )
            throw err
        } finally {
            setBusy( false )
            console.log( "Ran Disable!" )
        }
    }, [apiClient, audience] )

    return { supported, permission, subscribed, busy, error, enable, disable }
}

export default usePushNotifications